import { useEffect, useRef } from 'react'
import { useAIPageContextSafe } from '@/lib/contexts/AIPageContext'
import { formatNumeric } from '@/lib/utils/stringUtils'
import { formatCurrency } from '@/lib/utils/revenueUtils'
import type { AttributionData } from './useAttributionData'

export function useAIContextSync(data: AttributionData, isLoading: boolean) {
  const aiContext = useAIPageContextSafe()
  const aiContextRef = useRef(aiContext)
  aiContextRef.current = aiContext

  const {
    adTotals, totalRevenueDollars, attributedRevenueDollars, adRevenueDollars, adPaymentCount, trueROAS,
    metaConnections, googleAdsConnections, adsAttributionData, humblyticsData, landingPagesWithRevenue,
  } = data
  // The same campaign rows the Campaigns table renders
  const campaigns = adsAttributionData?.campaigns ?? []
  const platforms = [
    metaConnections.length > 0 ? `Meta Ads (${metaConnections.length} connection${metaConnections.length !== 1 ? 's' : ''})` : null,
    googleAdsConnections.length > 0 ? `Google Ads (${googleAdsConnections.length} connection${googleAdsConnections.length !== 1 ? 's' : ''})` : null,
  ].filter(Boolean).join(', ') || 'None'

  const aiDataKey = `${adTotals.spend}-${adTotals.clicks}-${totalRevenueDollars}-${attributedRevenueDollars}-${adRevenueDollars}-${campaigns.length}-${humblyticsData.sourceData.length}-${landingPagesWithRevenue.length}`

  useEffect(() => {
    const ctx = aiContextRef.current
    if (!ctx || isLoading) return

    ctx.setCustomData('Attribution Summary', [
      `Ad Spend: $${adTotals.spend.toFixed(2)}`,
      `Impressions: ${formatNumeric(adTotals.impressions)}`,
      `Clicks: ${formatNumeric(adTotals.clicks)}`,
      `CPC: ${adTotals.clicks > 0 ? `$${(adTotals.spend / adTotals.clicks).toFixed(2)}` : 'N/A'}`,
      `CTR: ${adTotals.impressions > 0 ? `${(adTotals.clicks / adTotals.impressions * 100).toFixed(1)}%` : 'N/A'}`,
      `Collected Revenue: ${formatCurrency(totalRevenueDollars)}`,
      `Revenue matched to any visit: ${formatCurrency(attributedRevenueDollars)}`,
      `Revenue from ad clicks: ${formatCurrency(adRevenueDollars)}`,
      `Paying customers from ads: ${adPaymentCount}`,
      `ROAS (revenue from ad clicks / ad spend): ${trueROAS > 0 ? `${trueROAS.toFixed(2)}x` : 'N/A'}`,
      `Cost per paying customer: ${adPaymentCount > 0 && adTotals.spend > 0 ? `$${(adTotals.spend / adPaymentCount).toFixed(2)}` : 'N/A'}`,
      `Connected Platforms: ${platforms}`,
    ].join('\n'))

    if (campaigns.length > 0) {
      const campaignRows = campaigns.map((c) => ({
        name: c.campaign,
        utm_campaign: c.utm_campaign,
        platform: c.platforms.join(', '),
        spend: `$${c.spend.toFixed(2)}`,
        clicks: c.clicks,
        impressions: c.impressions,
        sessions: c.sessions,
        revenue: c.revenue != null ? formatCurrency(c.revenue) : 'N/A',
        roas: c.roas != null && c.roas > 0 ? `${c.roas.toFixed(2)}x` : 'N/A',
      }))
      ctx.setCustomData('Campaigns', campaignRows)
    }

    if (humblyticsData.sourceData.length > 0) {
      const sourceRows = humblyticsData.sourceData.slice(0, 10).map((s) => ({
        source: s.source,
        type: s.isPaid ? 'Paid' : 'Organic',
        sessions: s.sessions,
        revenue: formatCurrency(s.revenueCents / 100),
        revPerSession: s.sessions > 0 ? `$${(s.revenueCents / 100 / s.sessions).toFixed(2)}` : '$0',
      }))
      ctx.setCustomData('Traffic Sources', sourceRows)
    }

    if (landingPagesWithRevenue.length > 0) {
      const pageRows = landingPagesWithRevenue.slice(0, 10).map((p) => ({
        page: p.page,
        visits: p.visits,
        revenue: formatCurrency(p.revenueCents / 100),
      }))
      ctx.setCustomData('Top Landing Pages', pageRows)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiDataKey, isLoading])
}
